import React, { useEffect, useState } from 'react';
import { guestAPI } from '../utils/api';
import { Loader2 } from 'lucide-react';

const S = {
  page: {
    minHeight: '100vh',
    background: '#F9F9F7',
    fontFamily: "'Inter', 'Segoe UI', sans-serif",
    color: '#2d3748',
  },
  container: {
    maxWidth: '850px',
    margin: '0 auto',
    background: '#ffffff',
    borderRadius: '20px',
    boxShadow: '0 10px 25px -5px rgba(10, 95, 56, 0.05), 0 8px 16px -8px rgba(197, 165, 125, 0.1)',
    borderTop: '6px solid #0A5F38',
    position: 'relative',
    overflow: 'hidden',
  },
  goldDivider: {
    height: '2px',
    background: 'linear-gradient(to right, #C5A57D 0%, rgba(197, 165, 125, 0) 100%)',
    marginBottom: '30px',
  },
  headerGroup: {
    marginBottom: '35px',
  },
  title: {
    fontWeight: 800,
    color: '#0A5F38',
    margin: '8px 0',
    letterSpacing: '-0.025em',
  },
  subtitle: {
    fontSize: '0.95rem',
    color: '#C5A57D',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  lastUpdated: {
    fontSize: '0.85rem',
    color: '#718096',
    fontStyle: 'italic',
    marginTop: '12px',
    display: 'block',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    marginTop: '40px',
    marginBottom: '20px',
    borderLeft: '4px solid #C5A57D',
    paddingLeft: '16px',
  },
  sectionTitle: {
    fontSize: '1.35rem',
    fontWeight: 700,
    color: '#0A5F38',
    margin: 0,
  },
  paragraph: {
    fontSize: '0.975rem',
    lineHeight: '1.7',
    color: '#4a5568',
    marginBottom: '20px',
  },
  list: {
    paddingLeft: '0px',
    marginBottom: '20px',
    listStyleType: 'none',
  },
  listItem: {
    fontSize: '0.975rem',
    lineHeight: '1.7',
    color: '#4a5568',
    marginBottom: '10px',
    position: 'relative',
    paddingLeft: '24px',
  },
  listDot: {
    position: 'absolute',
    left: '0',
    top: '10px',
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: '#C5A57D',
  },
  subSectionTitle: {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: '#2d3748',
    marginTop: '20px',
    marginBottom: '10px',
  },
  contactBox: {
    background: '#FDFCEB',
    border: '1px solid rgba(197, 165, 125, 0.3)',
    borderRadius: '12px',
    padding: '25px',
    marginTop: '40px',
  },
  contactTitle: {
    fontSize: '1.2rem',
    fontWeight: 700,
    color: '#0A5F38',
    marginBottom: '15px',
  },
  contactList: {
    listStyleType: 'none',
    paddingLeft: 0,
    margin: 0,
  },
  contactItem: {
    fontSize: '0.95rem',
    lineHeight: '1.6',
    color: '#4a5568',
    marginBottom: '10px',
  },
  contactLabel: {
    fontWeight: 600,
    color: '#0A5F38',
  },
  contactValue: {
    color: '#4a5568',
    wordBreak: 'break-all',
  }
};

const PrivacyPolicy = () => {
  const [loading, setLoading] = useState(true);
  const [companySettings, setCompanySettings] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await guestAPI.getCompanySettings();
        if (res.data?.success) {
          setCompanySettings(res.data.companySettings);
        }
      } catch (error) {
        console.error('Failed to fetch company settings:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  if (loading) {
    return (
      <div style={S.page} className="flex flex-col items-center justify-center min-h-screen">
        <Loader2 size={36} className="animate-spin text-[#0A5F38]" />
        <span className="text-sm mt-3 font-semibold text-slate-500">Loading Privacy Policy...</span>
      </div>
    );
  }

  const companyName = companySettings?.companyName || 'IdealTrendz Interiors';
  const email = 'idealtrendz.int@gmail.com';
  const gst = companySettings?.gstNumber || '';
  const addressLines = [
    companySettings?.addressLine1,
    companySettings?.addressLine2,
    companySettings?.city,
    companySettings?.state,
    companySettings?.pincode
  ].filter(Boolean).join(', ');

  const fullAddress = addressLines || 'Hyderabad, Telangana';

  return (
    <div style={S.page} className="py-8 px-4 sm:py-16 sm:px-6">
      <div style={S.container} className="px-6 py-8 sm:px-12 sm:py-12">
        <div style={S.headerGroup}>
          <span style={S.subtitle}>{companyName}</span>
          <h1 style={S.title} className="text-3xl sm:text-4xl">Privacy Policy</h1>
          <div style={S.goldDivider} />
          <span style={S.lastUpdated}>Last Updated: {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>
        </div>

        <div style={S.sectionHeader}>
          <h2 style={S.sectionTitle}>Introduction</h2>
        </div>
        <p style={S.paragraph}>
          <strong>{companyName}</strong> ("we," "us," "our," or "Company") is committed to protecting the privacy and security of your personal data. This Privacy Policy explains how we collect, use, store, and protect information when you use our platform, website, or connect with our WhatsApp Business Account.
        </p>

        <div style={S.sectionHeader}>
          <h2 style={S.sectionTitle}>Scope and Applicability</h2>
        </div>
        <p style={S.paragraph}>
          This Privacy Policy applies to:
        </p>
        <ul style={S.list}>
          <li style={S.listItem}><span style={S.listDot} />End Customers whose data is processed through WhatsApp conversations and site visits.</li>
          <li style={S.listItem}><span style={S.listDot} />All data collected via our website, mobile applications, and WhatsApp Business API integrations.</li>
          <li style={S.listItem}><span style={S.listDot} />Users who interact with our interior design platform and request estimates or catalogs.</li>
        </ul>
        <p style={S.paragraph}>
          We comply with the Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011 (India) and Meta Platform Policies for WhatsApp Business API integrations.
        </p>

        <div style={S.sectionHeader}>
          <h2 style={S.sectionTitle}>Data We Collect</h2>
        </div>
        <h3 style={S.subSectionTitle}>A. Data from End Customers (Through WhatsApp & Forms)</h3>
        <ul style={S.list}>
          <li style={S.listItem}><span style={S.listDot} /><strong>Message Data:</strong> Customer phone number (from WhatsApp), interaction timestamps.</li>
          <li style={S.listItem}><span style={S.listDot} /><strong>Customer Information:</strong> Customer name, delivery/site address, product preferences, and order details.</li>
          <li style={S.listItem}><span style={S.listDot} /><strong>Metadata:</strong> IP address (when accessing web links), message delivery status, and engagement metrics.</li>
        </ul>

        <div style={S.sectionHeader}>
          <h2 style={S.sectionTitle}>How We Use Your Data</h2>
        </div>
        <ul style={S.list}>
          <li style={S.listItem}><span style={S.listDot} /><strong>For Business Operations:</strong> To provide interior design consultations, estimates, product catalogs, and process payments securely.</li>
          <li style={S.listItem}><span style={S.listDot} /><strong>For AI Processing and Automation:</strong> To train and improve the AI agent's response accuracy, generate product recommendations, and process customer queries over WhatsApp.</li>
          <li style={S.listItem}><span style={S.listDot} /><strong>For Legal and Compliance:</strong> To comply with GST and tax obligations and enforce our Terms of Service.</li>
        </ul>

        <div style={S.sectionHeader}>
          <h2 style={S.sectionTitle}>Data Sharing and Third Parties</h2>
        </div>
        <p style={S.paragraph}>
          <strong>Meta Platforms:</strong> When you interact with our WhatsApp chatbot, data is exchanged with Meta through the official WhatsApp Business API. This includes sending automated messages and reading message threads. All data exchanged is governed by Meta's Platform Terms and Data Policy.
        </p>
        <p style={S.paragraph}>
          <strong>Cloud Providers:</strong> We use secure cloud providers for data storage. Data is encrypted in transit and at rest.
        </p>
        <p style={S.paragraph}>
          We do <strong>NOT</strong> share your data with third-party marketing companies, advertisers, or data brokers without explicit consent.
        </p>

        <div style={S.sectionHeader}>
          <h2 style={S.sectionTitle}>Data Security & Retention</h2>
        </div>
        <p style={S.paragraph}>
          We implement HTTPS/TLS in transit and robust database encryption at rest. Customer interaction data is retained as long as necessary for analytics and dispute resolution, and payment records are retained as required for GST compliance in India.
        </p>

        <div style={S.sectionHeader}>
          <h2 style={S.sectionTitle}>Data Deletion & Withdrawal of Consent</h2>
        </div>
        <p style={S.paragraph}>
          If you wish to delete your data or withdraw consent from WhatsApp communications, you may request this by contacting our privacy team. Please note that once you agree to this Privacy Policy to start using our WhatsApp chatbot service, you cannot undo or revoke this consent directly via chatbot button options (such as clicking 'Not Agree' on previous messages). To withdraw your consent or request data deletion, you must contact our privacy team as detailed below.
        </p>

        <div style={S.contactBox}>
          <h3 style={S.contactTitle}>Contact Us</h3>
          <p style={S.paragraph}>
            If you have questions or comments about this policy, or wish to exercise your data rights, you may contact us at:
          </p>
          <ul style={S.contactList}>
            <li style={S.contactItem} className="flex flex-col sm:flex-row sm:items-baseline">
              <span style={S.contactLabel} className="w-full sm:w-[120px] shrink-0">Legal Name:</span>
              <span style={S.contactValue}>{companyName}</span>
            </li>
            {gst && (
              <li style={S.contactItem} className="flex flex-col sm:flex-row sm:items-baseline">
                <span style={S.contactLabel} className="w-full sm:w-[120px] shrink-0">GST Number:</span>
                <span style={S.contactValue}>{gst}</span>
              </li>
            )}
            <li style={S.contactItem} className="flex flex-col sm:flex-row sm:items-baseline">
              <span style={S.contactLabel} className="w-full sm:w-[120px] shrink-0">Address:</span>
              <span style={S.contactValue}>
                {companySettings ? (
                  <>
                    {companySettings.addressLine1 && <>{companySettings.addressLine1}<br /></>}
                    {companySettings.addressLine2 && <>{companySettings.addressLine2}<br /></>}
                    {[companySettings.city, companySettings.state, companySettings.pincode].filter(Boolean).join(', ')}
                  </>
                ) : (
                  fullAddress
                )}
              </span>
            </li>
            <li style={S.contactItem} className="flex flex-col sm:flex-row sm:items-baseline">
              <span style={S.contactLabel} className="w-full sm:w-[120px] shrink-0">Email:</span>
              <span style={S.contactValue}>
                <a href={`mailto:${email}`} style={{ color: '#0A5F38', fontWeight: 600, textDecoration: 'underline' }}>{email}</a>
              </span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
